import { useState, useMemo, useCallback } from 'react';
import { ControlOption } from './types';
import { ChevronDown, ArrowLeft } from 'lucide-react';

interface CompactControlsProps<M = string, V = string> {
  metricOptions: ControlOption<M>[];
  viewOptions: ControlOption<V>[];
  selectedMetric: M;
  selectedView: V;
  onMetricChange: (value: M) => void;
  onViewChange: (value: V) => void;
  onResetView?: () => void;
  showBackButton?: boolean;
  className?: string;
  dropdownViewOptions?: V[];
}

export function CompactControls<M extends string, V extends string>({
  metricOptions,
  viewOptions,
  selectedMetric,
  selectedView,
  onMetricChange,
  onViewChange,
  onResetView,
  showBackButton = false,
  className = '',
  dropdownViewOptions = [],
}: CompactControlsProps<M, V>) {
  const [isMetricOpen, setIsMetricOpen] = useState(false);
  const [isViewDropdownOpen, setIsViewDropdownOpen] = useState(false);

  const selectedMetricOption = useMemo(
    () => metricOptions.find(option => option.value === selectedMetric),
    [metricOptions, selectedMetric],
  );

  const dropdownViewOptionsList = useMemo(
    () => viewOptions.filter(option => dropdownViewOptions.includes(option.value)),
    [viewOptions, dropdownViewOptions],
  );

  const separateViewOptions = useMemo(
    () => viewOptions.filter(option => !dropdownViewOptions.includes(option.value)),
    [viewOptions, dropdownViewOptions],
  );

  const selectedDropdownViewOption = useMemo(
    () => dropdownViewOptionsList.find(option => option.value === selectedView),
    [dropdownViewOptionsList, selectedView],
  );

  const handleMetricToggle = useCallback(() => {
    setIsMetricOpen(prev => !prev);
  }, []);

  const handleMetricSelect = useCallback(
    (value: M) => {
      onMetricChange(value);
      setIsMetricOpen(false);
    },
    [onMetricChange],
  );

  const handleViewSelect = useCallback(
    (value: V) => {
      onViewChange(value);
    },
    [onViewChange],
  );

  const handleViewDropdownToggle = useCallback(() => {
    setIsViewDropdownOpen(prev => !prev);
  }, []);

  const handleViewDropdownSelect = useCallback(
    (value: V) => {
      onViewChange(value);
      setIsViewDropdownOpen(false);
    },
    [onViewChange],
  );

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {showBackButton && onResetView && (
        <button
          onClick={onResetView}
          className="flex items-center gap-1 rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface)] px-2 py-1.5 text-sm font-medium text-[var(--anl-text)] transition-colors hover:bg-[var(--anl-surface-elev)]"
          title="Back to Chart"
        >
          <ArrowLeft className="h-3 w-3" />
          <span className="hidden sm:inline">Back</span>
        </button>
      )}

      {metricOptions.length > 0 && (
        <div className="relative">
          <button
            onClick={handleMetricToggle}
            className="flex items-center space-x-1 rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface)] px-3 py-1.5 text-sm font-medium text-[var(--anl-text)] transition-colors hover:bg-[var(--anl-surface-elev)]"
          >
            {selectedMetricOption?.icon && (
              <span className="h-4 w-4">{selectedMetricOption.icon}</span>
            )}
            <span className="hidden sm:inline">{selectedMetricOption?.label}</span>
            <ChevronDown className="h-3 w-3" />
          </button>

          {isMetricOpen && (
            <div className="absolute left-0 top-full z-20 mt-1 w-40 rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface-elev)] shadow-lg">
              {metricOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => handleMetricSelect(option.value)}
                  className={`flex w-full items-center space-x-2 px-3 py-2 text-left text-sm transition-colors ${
                    option.value === selectedMetric
                      ? 'bg-[var(--anl-surface)] text-[var(--anl-text)]'
                      : 'text-[var(--anl-text-muted)] hover:text-[var(--anl-text)]'
                  }`}
                >
                  {option.icon && <span className="h-4 w-4">{option.icon}</span>}
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {dropdownViewOptionsList.length > 0 && selectedDropdownViewOption && (
        <div className="relative">
          <button
            onClick={handleViewDropdownToggle}
            className="flex items-center space-x-1 rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface)] px-3 py-1.5 text-sm font-medium text-[var(--anl-text)] transition-colors hover:bg-[var(--anl-surface-elev)]"
          >
            {selectedDropdownViewOption.icon && (
              <span className="h-4 w-4">{selectedDropdownViewOption.icon}</span>
            )}
            <span className="hidden sm:inline">{selectedDropdownViewOption.label}</span>
            <ChevronDown className="h-3 w-3" />
          </button>

          {isViewDropdownOpen && (
            <div className="absolute left-0 top-full z-20 mt-1 w-32 rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface-elev)] shadow-lg">
              {dropdownViewOptionsList.map(option => (
                <button
                  key={option.value}
                  onClick={() => handleViewDropdownSelect(option.value)}
                  className={`flex w-full items-center space-x-2 px-3 py-2 text-left text-sm transition-colors ${
                    option.value === selectedView
                      ? 'bg-[var(--anl-surface)] text-[var(--anl-text)]'
                      : 'text-[var(--anl-text-muted)] hover:text-[var(--anl-text)]'
                  }`}
                >
                  {option.icon && <span className="h-4 w-4">{option.icon}</span>}
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {separateViewOptions.length > 0 && (
        <div className="flex items-center rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface)] p-1">
          {separateViewOptions.map(option => (
            <button
              key={option.value}
              onClick={() => handleViewSelect(option.value)}
              className={`flex items-center space-x-1 rounded px-2 py-1 text-sm font-medium transition-colors ${
                option.value === selectedView
                  ? 'bg-[var(--anl-surface-elev)] text-[var(--anl-text)] shadow-sm'
                  : 'text-[var(--anl-text-muted)] hover:text-[var(--anl-text)]'
              }`}
            >
              {option.icon && <span className="h-4 w-4">{option.icon}</span>}
              <span className="hidden sm:inline">{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
